import React, { useEffect, useRef, useState } from 'react';

function WhepVideoPlayer({ edgeUrl, className = '', onStreamEnd, retryCount = 10, retryDelay = 2000 }) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionUrlRef = useRef(null);
  const cancelledRef = useRef(false);
  const pendingCandidatesRef = useRef([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    if (!edgeUrl) {
      setState('error');
      return;
    }

    let pc = null;
    let sessionUrl = null;
    cancelledRef.current = false;
    const pendingCandidates = [];
    let cancelled = false;
    let connectedOnce = false;
    const retryTimers = [];

    async function attemptWhep(url, attempt) {
      if (cancelled) return;
      pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;
      sessionUrl = null;
      pendingCandidates.length = 0;

      pc.ontrack = (event) => {
        if (cancelled) return;
        const v = videoRef.current;
        if (!v) return;
        connectedOnce = true;
        setState('connected');
        v.srcObject = event.streams[0];
        v.play().catch(() => {});
      };

      pc.onicecandidate = (event) => {
        if (cancelled || !event.candidate) return;
        const fragment = `a=${event.candidate.candidate}\r\n`;
        if (sessionUrl) {
          fetch(sessionUrl, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/trickle-ice-sdpfrag',
              'If-Match': '*',
            },
            body: fragment,
          }).catch(() => {});
        } else {
          pendingCandidates.push(fragment);
        }
      };

      pc.onconnectionstatechange = () => {
        if (cancelled) return;
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
          onStreamEnd?.();
        }
      };

      try {
        const offer = await pc.createOffer({ offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);

        const whepEndpoint = url.replace(/\/whep\/?$/i, '').replace(/\/?$/, '') + '/whep';
        const res = await fetch(whepEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: offer.sdp,
        });
        if (!res.ok) throw new Error(`WHEP failed: ${res.status}`);

        const locHeader = res.headers.get('Location');
        if (!locHeader) throw new Error('WHEP missing Location header');
        sessionUrl = new URL(locHeader, url).toString();
        sessionUrlRef.current = sessionUrl;

        const answerSdp = await res.text();
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        for (const frag of pendingCandidates) {
          fetch(sessionUrl, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/trickle-ice-sdpfrag',
              'If-Match': '*',
            },
            body: frag,
          }).catch(() => {});
        }
      } catch (err) {
        console.error('WHEP error:', err);
        if (pc && pc.signalingState !== 'closed') {
          pc.close();
        }
        if (cancelled) return;
        if (attempt < retryCount) {
          const tid = setTimeout(() => attemptWhep(url, attempt + 1), retryDelay);
          retryTimers.push(tid);
        } else {
          if (!connectedOnce) setState('error');
        }
      }
    }

    attemptWhep(edgeUrl, 0);

    return () => {
      cancelled = true;
      cancelledRef.current = true;
      retryTimers.forEach(clearTimeout);
      if (sessionUrl) {
        fetch(sessionUrl, { method: 'DELETE' }).catch(() => {});
      }
      if (pc) {
        pc.ontrack = null;
        pc.onicecandidate = null;
        pc.onconnectionstatechange = null;
        pc.close();
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [edgeUrl]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
      <video
        ref={videoRef}
        className={className}
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: state === 'connected' ? 'block' : 'none' }}
        muted
        playsInline
        autoPlay
      />
      {state === 'loading' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: '#888', fontSize: 14
        }}>
          Connecting to drone stream...
        </div>
      )}
      {state === 'error' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: '#ff4444', fontSize: 14
        }}>
          Drone stream unavailable
        </div>
      )}
    </div>
  );
}

export default WhepVideoPlayer;
